import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiPackageBold,
    PiChartBarBold,
    PiCurrencyDollarBold,
    PiPencilSimpleBold,
    PiRulerBold,
    PiShoppingCartBold,
    PiTagBold,
    PiTruckBold,
    PiWarningCircleBold,
    PiArrowsClockwiseBold,
    PiTranslateBold,
    PiFloppyDiskBold,
    PiCheckCircleBold,
    PiStarBold,
} from 'react-icons/pi';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import CdRackGrid, { type CdRuaGrid } from '@v2/features/cd-rack/CdRackGrid';
import { useI18n } from '@v2/providers/I18nProvider';
import ProductColorPhotos from '@v2/features/products/ProductColorPhotos';
import { StockPhaseBadge, StockShipmentLink } from '@v2/features/products/StockPhaseBadge';

const FALLBACK_IMG = '/img/favicon.png';

/* ─── Types ──────────────────────────────────────────────────── */
type ColorSwatch = { name: string; hex: string };
type ProductDetail = {
    id: number; code: string | null; name: string;
    description: string | null; image_url: string | null;
    group_name: string | null; family_name: string | null;
    product_type_label: string | null; product_type_color: string | null;
    category: string | null; series: string | null; line: string | null;
    condition: string | null; status: string | null; suspended: boolean;
    supplier: string | null; supplier_code: string | null; protheus_code: string | null;
    unit_price: number | null; cost_brl: number | null; cost_usd: number | null;
    purchase_cost_brl: number | null;
    assembled_size: string | null; box_size: string | null; boxes_count: number | null;
    box_label: string | null; plates_count: number | null; net_weight_kg: number | null;
    gross_weight_kg: number | null; weight_tower_qty: number | null; cbm: number | null;
    stock_minimum: number;
    stock_summary: { total: number; available: number; reserved: number; in_transit: number; sold: number; defective: number; incoming: number; total_projected: number };
    stock_prediction: { avg_monthly: number; months_covered: number | null; need_3_months: number; need_6_months: number; current_available: number; in_transit: number; incoming: number; total_projected: number };
    stock_alert_lines: string[];
    color_options: Record<string, ColorSwatch[]>;
    customs_descriptions: { pt_BR: string | null; en: string | null; es: string | null };
    customs_description_default: string | null;
    stock_items_count?: number;
    cd_storage: { has_positions: boolean; highlight_codes: string[]; ruas: CdRuaGrid[]; locations: Array<{ code: string; rua: string; bay: number | null; level: number | null; level_label: string | null; count: number; serials: string[] }> };
    v1_url: string;
};

type StockItem = {
    id: number; serial_number: string | null;
    frame_color: string | null; seat_color: string | null;
    stitches_color: string | null; arm_color: string | null;
    invoice_number: string | null; invoice_url: string | null;
    nf_number: string | null; status: string; status_label: string;
    stock_phase?: 'terra' | 'transito' | string | null;
    stock_phase_label?: string | null;
    shipment_id?: number | null;
    shipment_label?: string | null;
    location: string | null; cd_location_label: string | null;
    customer_name: string | null; order_number: string | null;
    reserved_for: string | null; entry_date_label: string | null;
    created_by_name: string | null; created_at_label: string | null;
};

type TabId = 'stock' | 'supplier' | 'costs' | 'dimensions' | 'prediction' | 'colors' | 'description' | 'translations';

type ProductTranslation = {
    id: number | null;
    locale: string;
    translated_name: string | null;
    functional_description: string | null;
    notes: string | null;
    is_default: boolean;
};

const LOCALE_LABELS: Record<string, string> = {
    pt_BR: '🇧🇷 Português (BR)',
    en: '🇺🇸 English',
    es: '🇪🇸 Español',
    zh_CN: '🇨🇳 中文',
};

/* ─── Utils ───────────────────────────────────────────────────── */
const fmtBRL = (n: number | null | undefined) =>
    n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtUSD = (n: number | null | undefined) =>
    n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/* ─── Sub-componentes ─────────────────────────────────────────── */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1">
            <dt className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-zinc-500">{label}</dt>
            <dd className="text-sm font-medium text-gray-800 dark:text-zinc-200">
                {value ?? <span className="text-gray-400 dark:text-zinc-600">—</span>}
            </dd>
        </div>
    );
}

function StockKpi({ label, value, accent = false, warn = false }: {
    label: string; value: number; accent?: boolean; warn?: boolean;
}) {
    const isEmpty = warn && value === 0;
    return (
        <div className={cn(
            'flex flex-col items-center rounded-xl border p-3 text-center',
            isEmpty
                ? 'border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/8'
                : accent
                    ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/8'
                    : 'border-gray-200 bg-gray-50 dark:border-zinc-700/60 dark:bg-zinc-800/60',
        )}>
            <span className={cn(
                'text-2xl font-black tabular-nums leading-none',
                isEmpty ? 'text-red-500 dark:text-red-400'
                    : accent ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-800 dark:text-white',
            )}>
                {value}
            </span>
            <span className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">{label}</span>
        </div>
    );
}

const STATUS_BADGE: Record<string, { bg: string; text: string; dot: string }> = {
    disponivel:  { bg: 'bg-emerald-100 border-emerald-300 dark:bg-emerald-500/10 dark:border-emerald-500/25', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500' },
    reservado:   { bg: 'bg-amber-100 border-amber-300 dark:bg-amber-500/10 dark:border-amber-500/25',         text: 'text-amber-700 dark:text-amber-400',   dot: 'bg-amber-500'   },
    vendido:     { bg: 'bg-blue-100 border-blue-300 dark:bg-blue-500/10 dark:border-blue-500/25',             text: 'text-blue-700 dark:text-blue-400',     dot: 'bg-blue-500'    },
    em_transito: { bg: 'bg-indigo-100 border-indigo-300 dark:bg-indigo-500/10 dark:border-indigo-500/25',     text: 'text-indigo-700 dark:text-indigo-400', dot: 'bg-indigo-500'  },
    defeito:     { bg: 'bg-red-100 border-red-300 dark:bg-red-500/10 dark:border-red-500/25',                 text: 'text-red-700 dark:text-red-400',       dot: 'bg-red-500'     },
    garantia:    { bg: 'bg-purple-100 border-purple-300 dark:bg-purple-500/10 dark:border-purple-500/25',     text: 'text-purple-700 dark:text-purple-400', dot: 'bg-purple-500'  },
};

function StatusBadge({ status, label }: { status: string; label: string }) {
    const cfg = STATUS_BADGE[status] ?? {
        bg: 'bg-gray-100 border-gray-300 dark:bg-zinc-700/30 dark:border-zinc-600/30',
        text: 'text-gray-600 dark:text-zinc-400',
        dot: 'bg-gray-400 dark:bg-zinc-500',
    };
    return (
        <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-black', cfg.bg, cfg.text)}>
            <span className={cn('size-1.5 rounded-full', cfg.dot)} />
            {label}
        </span>
    );
}

function TypeChip({ label, color }: { label: string; color: string | null }) {
    const map: Record<string, string> = {
        primary: 'border-amber-400/50 bg-amber-50 text-amber-700 dark:border-[#C9A84C]/30 dark:bg-[#C9A84C]/10 dark:text-[#F0D06A]',
        info:    'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-400',
        success: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400',
        warning: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400',
        danger:  'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400',
    };
    return (
        <span className={cn(
            'rounded-full border px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wider',
            map[color ?? ''] ?? 'border-gray-300 bg-gray-100 text-gray-600 dark:border-zinc-600/30 dark:bg-zinc-700/30 dark:text-zinc-400',
        )}>
            {label}
        </span>
    );
}

function ColorSwatchList({ items, empty }: { items: ColorSwatch[]; empty: string }) {
    if (!items.length) return <span className="text-sm text-gray-400 dark:text-zinc-600">{empty}</span>;
    return (
        <div className="flex flex-wrap gap-2">
            {items.map((c) => (
                <span key={c.name} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                    <span className="size-4 shrink-0 rounded-full border border-gray-300 shadow-inner dark:border-zinc-600" style={{ backgroundColor: c.hex }} />
                    {c.name}
                </span>
            ))}
        </div>
    );
}

const TABS: { id: TabId; icon: React.ReactNode }[] = [
    { id: 'stock',        icon: <PiPackageBold className="size-4" />         },
    { id: 'prediction',   icon: <PiChartBarBold className="size-4" />        },
    { id: 'costs',        icon: <PiCurrencyDollarBold className="size-4" />  },
    { id: 'dimensions',   icon: <PiRulerBold className="size-4" />           },
    { id: 'colors',       icon: <PiTagBold className="size-4" />             },
    { id: 'supplier',     icon: <PiTruckBold className="size-4" />           },
    { id: 'description',  icon: <PiShoppingCartBold className="size-4" />    },
    { id: 'translations', icon: <PiTranslateBold className="size-4" />       },
];

/* ─── PÁGINA ─────────────────────────────────────────────────── */
export default function ProductDetailPage() {
    const { t, localeTag } = useI18n();
    const { id } = useParams<{ id: string }>();
    const [tab, setTab] = useState<TabId>('stock');
    const [stockSearch, setStockSearch] = useState('');
    const [stockStatus, setStockStatus] = useState('');

    const productQuery = useQuery({
        queryKey: ['products', id, 'detail', window.__V2__?.locale],
        enabled: !!id,
        queryFn: async () => {
            const { data } = await api.get(`/products/${id}`);
            return (data.data ?? data) as ProductDetail;
        },
    });

    const stockQuery = useQuery({
        queryKey: ['products', id, 'stock-items', stockSearch, stockStatus],
        enabled: !!id && tab === 'stock',
        queryFn: async () => {
            const params: Record<string, string> = { per_page: '50' };
            if (stockSearch) params.search = stockSearch;
            if (stockStatus) params.status = stockStatus;
            const { data } = await api.get<{ data: StockItem[] }>(`/products/${id}/stock-items`, { params });
            return data.data ?? [];
        },
    });

    const translationsQuery = useQuery({
        queryKey: ['products', id, 'translations'],
        enabled: !!id && tab === 'translations',
        queryFn: async () => {
            const { data } = await api.get<{ data: ProductTranslation[] }>(`/products/${id}/translations`);
            return data.data ?? [];
        },
        staleTime: 30_000,
    });

    const tabs = useMemo(() => TABS.map((tb) => ({
        ...tb,
        label: tb.id === 'stock' ? t('product.tab_stock')
            : tb.id === 'prediction' ? t('product.tab_prediction')
            : tb.id === 'costs' ? t('product.tab_costs')
            : tb.id === 'dimensions' ? t('product.tab_dimensions')
            : tb.id === 'colors' ? t('product.tab_colors')
            : tb.id === 'supplier' ? t('product.tab_supplier_codes')
            : tb.id === 'translations' ? 'Tradução'
            : t('product.tab_description'),
    })), [t]);

    if (productQuery.isLoading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <div className="flex items-center gap-3 text-gray-400 dark:text-zinc-500">
                    <PiArrowsClockwiseBold className="size-5 animate-spin" />
                    <span className="text-sm">{t('v2.common.loading_product')}</span>
                </div>
            </div>
        );
    }

    const p = productQuery.data;
    if (productQuery.error || !p) {
        return (
            <div className="flex min-h-[30vh] flex-col items-center justify-center gap-4">
                <PiWarningCircleBold className="size-10 text-red-400" />
                <p className="text-sm text-red-500">{t('v2.common.load_product_error')}</p>
                <Link to="/products" className="text-sm text-gray-500 hover:underline dark:text-zinc-500">← Voltar</Link>
            </div>
        );
    }

    const s = p.stock_summary;
    const pred = p.stock_prediction;
    const isLowStock = s.available <= p.stock_minimum && p.stock_minimum > 0;

    /* ── Shells reutilizáveis ──────────────────────── */
    const cardCls = 'rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';
    const sectionTitleCls = 'mb-4 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500';

    return (
        <div className="space-y-0">

            {/* ── Breadcrumb / Nav ─────────────────────────── */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3 dark:border-zinc-800/60">
                <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-zinc-600">
                    <Link to="/" className="hover:text-gray-600 dark:hover:text-zinc-400">Dashboard</Link>
                    <span>/</span>
                    <Link to="/products" className="hover:text-gray-600 dark:hover:text-zinc-400">{t('v2.common.products')}</Link>
                    <span>/</span>
                    <span className="font-mono text-gray-600 dark:text-zinc-400">{p.code ?? String(p.id)}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to="/products"
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-700 dark:hover:text-zinc-200"
                    >
                        <PiArrowLeftBold className="size-3.5" />
                        {t('v2.common.back')}
                    </Link>
                    <Link
                        to={`/products/${id}/edit`}
                        className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 transition hover:bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                    >
                        <PiPencilSimpleBold className="size-3.5" />
                        {t('v2.common.edit_colors')}
                    </Link>

                </div>
            </div>

            {/* ── Hero ─────────────────────────────────────── */}
            <div className="border-b border-gray-200 bg-gray-50/60 px-6 py-6 dark:border-zinc-800/60 dark:bg-zinc-900/40">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start">

                    {/* Imagem */}
                    <div className="flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white lg:size-48 dark:border-zinc-800 dark:bg-zinc-900">
                        <img
                            src={p.image_url || FALLBACK_IMG}
                            alt={p.name}
                            className="max-h-44 max-w-full object-contain p-3"
                            onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                        />
                    </div>

                    {/* Info principal */}
                    <div className="min-w-0 flex-1">
                        {/* Badges */}
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                            {p.code && (
                                <span className="font-mono text-[11px] font-black tracking-widest text-amber-600 dark:text-[#C9A84C]">{p.code}</span>
                            )}
                            {p.product_type_label && <TypeChip label={p.product_type_label} color={p.product_type_color} />}
                            {p.suspended && (
                                <span className="rounded-full border border-red-300 bg-red-50 px-2.5 py-0.5 text-[11px] font-black text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
                                    {t('product.suspended')}
                                </span>
                            )}
                            {isLowStock && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-black text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
                                    <PiWarningCircleBold className="size-3" />
                                    Estoque baixo
                                </span>
                            )}
                        </div>

                        {/* Nome */}
                        <h1 className="mb-1 text-2xl font-black leading-tight text-gray-900 md:text-3xl dark:text-white">{p.name}</h1>

                        {/* Tags secundárias */}
                        <div className="mb-5 flex flex-wrap gap-1.5">
                            {[p.group_name, p.category, p.series ? `Série ${p.series}` : null, p.line].filter(Boolean).map((tag) => (
                                <span key={tag} className="rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 dark:border-zinc-700/50 dark:bg-zinc-800/50 dark:text-zinc-400">
                                    {tag}
                                </span>
                            ))}
                        </div>

                        {/* Campos rápidos */}
                        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                            <Field label={t('product.family')} value={p.family_name} />
                            <Field label={t('product.condition')} value={p.condition} />
                            <Field label={t('product.situation')} value={p.status} />
                            <Field label={t('v2.common.unit_price_label')} value={
                                p.unit_price != null
                                    ? <span className="font-mono text-amber-600 dark:text-[#F0D06A]">{fmtBRL(p.unit_price)}</span>
                                    : null
                            } />
                            <Field label={t('v2.common.unit_cbm')} value={
                                p.cbm != null
                                    ? <span className="font-mono">{p.cbm.toLocaleString(localeTag, { maximumFractionDigits: 4 })} m³</span>
                                    : null
                            } />
                        </dl>
                    </div>

                    {/* KPIs de estoque */}
                    <div className="flex shrink-0 flex-col gap-2 lg:w-52">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">Estoque</p>
                        <div className="grid grid-cols-2 gap-2">
                            <StockKpi label="Total"      value={s.total}      accent={s.total > 0} warn />
                            <StockKpi label="Disponível" value={s.available}  accent />
                            <StockKpi label="Reservado"  value={s.reserved} />
                            <StockKpi label="Em trânsito" value={s.in_transit} />
                        </div>
                        {p.stock_minimum > 0 && (
                            <p className="text-[10px] text-gray-400 dark:text-zinc-600">
                                Mínimo: <span className="font-bold text-gray-600 dark:text-zinc-400">{p.stock_minimum}</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tabs ─────────────────────────────────────── */}
            <div className="border-b border-gray-200 px-6 dark:border-zinc-800/60">
                <nav className="-mb-px flex gap-1 overflow-x-auto">
                    {tabs.map((tb) => (
                        <button
                            key={tb.id}
                            type="button"
                            onClick={() => setTab(tb.id)}
                            className={cn(
                                'flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition',
                                tab === tb.id
                                    ? 'border-amber-500 text-amber-600 dark:border-[#C9A84C] dark:text-[#F0D06A]'
                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-zinc-500 dark:hover:border-zinc-700 dark:hover:text-zinc-300',
                            )}
                        >
                            {tb.icon}
                            {tb.label}
                            {tb.id === 'stock' && p.stock_items_count != null && (
                                <span className={cn(
                                    'rounded-full px-2 py-0.5 text-[10px] font-black',
                                    tab === 'stock'
                                        ? 'bg-amber-100 text-amber-700 dark:bg-[#C9A84C]/20 dark:text-[#C9A84C]'
                                        : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-500',
                                )}>
                                    {p.stock_items_count}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>
            </div>

            {/* ── Conteúdo ─────────────────────────────────── */}
            <div className="px-6 py-6">

                {/* ESTOQUE */}
                {tab === 'stock' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-4 gap-3 sm:grid-cols-7">
                            <StockKpi label={t('v2.common.total')}           value={s.total}      accent={s.total > 0} warn />
                            <StockKpi label={t('product.available')}         value={s.available}  accent />
                            <StockKpi label={t('product.reserved')}          value={s.reserved} />
                            <StockKpi label={t('product.in_transit')}        value={s.in_transit} />
                            <StockKpi label={t('product.sold')}              value={s.sold} />
                            <StockKpi label={t('product.defective')}         value={s.defective}  warn />
                            <StockKpi label={t('product.incoming_stock')}    value={s.incoming} />
                        </div>

                        <p className="text-sm text-gray-500 dark:text-zinc-500">
                            {t('v2.common.projected_total')}{' '}
                            <span className="font-black text-gray-900 dark:text-white">{s.total_projected}</span>{' '}
                            {t('v2.common.units_short')}
                        </p>

                        {/* Mapa CD */}
                        {p.cd_storage?.has_positions && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 dark:border-[#C9A84C]/20 dark:bg-[#C9A84C]/5">
                                <p className="mb-1 text-sm font-black text-amber-700 dark:text-[#C9A84C]">
                                    {t('v2.common.cd_location_title')}
                                </p>
                                <p className="mb-4 text-xs text-gray-500 dark:text-zinc-500">{t('v2.common.cd_location_hint')}</p>
                                <div className="mb-4 flex flex-wrap gap-2">
                                    {p.cd_storage.locations.map((loc) => (
                                        <span
                                            key={loc.code}
                                            className="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-bold text-amber-800 dark:border-[#C9A84C]/30 dark:bg-[#C9A84C]/10 dark:text-[#C9A84C]"
                                        >
                                            <span className="font-mono">{loc.code}</span>
                                            {loc.level_label && <span className="ml-1 font-normal text-gray-500 dark:text-zinc-500">· {loc.level_label}</span>}
                                            <span className="ml-1 text-gray-400 dark:text-zinc-600">({loc.count} un.)</span>
                                        </span>
                                    ))}
                                </div>
                                <div className="flex flex-col gap-8">
                                    {p.cd_storage.ruas.map((rua) => (
                                        <div key={rua.code}>
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="text-sm font-black text-gray-700 dark:text-zinc-300">{rua.label}</span>
                                                <span className="rounded-full border border-amber-300 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:border-[#C9A84C]/30 dark:text-[#C9A84C]">
                                                    Rua {rua.code}
                                                </span>
                                            </div>
                                            <CdRackGrid
                                                rua={rua}
                                                variant="detailed"
                                                highlightCodes={p.cd_storage.highlight_codes}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Filtros */}
                        <div className="flex flex-wrap gap-3">
                            <input
                                type="text"
                                placeholder={t('v2.common.search_stock_placeholder')}
                                value={stockSearch}
                                onChange={(e) => setStockSearch(e.target.value)}
                                className="max-w-xs rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-white dark:placeholder:text-zinc-600 dark:focus:border-[#C9A84C]"
                            />
                            <select
                                value={stockStatus}
                                onChange={(e) => setStockStatus(e.target.value)}
                                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-amber-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:focus:border-[#C9A84C]"
                            >
                                <option value="">{t('v2.common.all_statuses')}</option>
                                <option value="disponivel">{t('stock_item.status_available')}</option>
                                <option value="reservado">{t('stock_item.status_reserved')}</option>
                                <option value="em_transito">{t('stock_item.status_in_transit')}</option>
                                <option value="vendido">{t('stock_item.status_sold')}</option>
                                <option value="defeito">{t('stock_item.status_defective')}</option>
                                <option value="garantia">{t('stock_item.status_warranty')}</option>
                            </select>
                        </div>

                        {/* Tabela */}
                        {stockQuery.isLoading ? (
                            <div className="flex items-center gap-2 py-8 text-gray-400 dark:text-zinc-500">
                                <PiArrowsClockwiseBold className="size-4 animate-spin" />
                                <span className="text-sm">{t('v2.common.loading_items')}</span>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-zinc-800">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900">
                                            {[
                                                t('v2.common.serial'),
                                                t('v2.common.colors_col'),
                                                t('common.status'),
                                                t('v2.stock_phase.col_phase'),
                                                t('v2.stock_phase.col_shipment'),
                                                t('common.location'),
                                                'CD',
                                                t('v2.common.client_order'),
                                                t('common.invoice'),
                                                t('v2.common.entry'),
                                            ].map((h) => (
                                                <th key={h} className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-500">
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                                        {(stockQuery.data ?? []).map((item) => (
                                            <tr key={item.id} className="transition hover:bg-gray-50 dark:hover:bg-zinc-800/40">
                                                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-700 dark:text-zinc-300">
                                                    {item.serial_number ?? '—'}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-500">
                                                    {[item.frame_color, item.seat_color, item.stitches_color, item.arm_color].filter(Boolean).join(' / ') || '—'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StatusBadge status={item.status} label={item.status_label} />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StockPhaseBadge
                                                        phase={item.stock_phase ?? (item.status === 'em_transito' ? 'transito' : 'terra')}
                                                        label={item.stock_phase_label}
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <StockShipmentLink
                                                        shipmentId={item.shipment_id}
                                                        label={item.shipment_label}
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-400">{item.location ?? '—'}</td>
                                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-zinc-400">{item.cd_location_label ?? '—'}</td>
                                                <td className="px-4 py-3 text-xs">
                                                    <span className="text-gray-700 dark:text-zinc-300">{item.customer_name ?? item.order_number ?? '—'}</span>
                                                    {item.reserved_for && (
                                                        <div className="text-[11px] text-amber-600 dark:text-[#C9A84C]">↳ {item.reserved_for}</div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-xs">
                                                    {item.invoice_url ? (
                                                        <a href={item.invoice_url} target="_blank" rel="noreferrer" className="font-mono text-amber-600 hover:underline dark:text-[#C9A84C]">
                                                            {item.invoice_number}
                                                        </a>
                                                    ) : (
                                                        <span className="font-mono text-gray-400 dark:text-zinc-500">{item.invoice_number ?? '—'}</span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400 dark:text-zinc-600">
                                                    {item.entry_date_label ?? item.created_at_label ?? '—'}
                                                </td>
                                            </tr>
                                        ))}
                                        {(stockQuery.data ?? []).length === 0 && (
                                            <tr>
                                                <td colSpan={10} className="py-12 text-center text-sm text-gray-400 dark:text-zinc-600">
                                                    {t('v2.common.no_stock_items')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                    </div>
                )}

                {/* PROJEÇÃO */}
                {tab === 'prediction' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                            {[
                                { label: t('product.avg_monthly'),   value: `${pred.avg_monthly.toLocaleString(localeTag)} / mês` },
                                { label: t('product.months_covered'), value: pred.months_covered != null ? `${pred.months_covered} meses` : t('v2.common.no_history') },
                                { label: t('product.need_3_months'), value: String(pred.need_3_months) },
                                { label: t('product.need_6_months'), value: String(pred.need_6_months) },
                            ].map((item) => (
                                <div key={item.label} className={cardCls + ' p-4'}>
                                    <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">{item.label}</p>
                                    <p className="text-lg font-black text-gray-900 dark:text-white">{item.value}</p>
                                </div>
                            ))}
                        </div>
                        {p.stock_alert_lines.length > 0 && (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/8">
                                <p className="mb-3 text-xs font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">Alertas</p>
                                <ul className="space-y-1.5">
                                    {p.stock_alert_lines.map((line, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-zinc-300">
                                            <PiWarningCircleBold className="mt-0.5 size-4 shrink-0 text-amber-500 dark:text-amber-400" />
                                            {line}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* CUSTOS */}
                {tab === 'costs' && (
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                        {[
                            { label: t('v2.common.unit_price_label'), value: fmtBRL(p.unit_price) },
                            { label: t('product.cost_brl'),           value: fmtBRL(p.cost_brl) },
                            { label: t('product.cost_usd'),           value: fmtUSD(p.cost_usd) },
                            { label: t('v2.common.purchase_cost'),    value: fmtBRL(p.purchase_cost_brl) },
                        ].map((item) => (
                            <div key={item.label} className={cardCls + ' p-4'}>
                                <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">{item.label}</p>
                                <p className="font-mono text-lg font-black text-amber-600 dark:text-[#F0D06A]">{item.value}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* DIMENSÕES */}
                {tab === 'dimensions' && (
                    <div className="space-y-5">
                        <div className={cardCls + ' p-5'}>
                            <p className={sectionTitleCls}>{t('product.packaging_section')}</p>
                            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                <Field label={t('product.boxes_count')} value={p.boxes_count != null ? `${p.boxes_count} caixas` : null} />
                                <Field label={t('product.box_label')} value={p.box_label} />
                                <Field label={t('v2.common.unit_cbm')} value={p.cbm != null ? `${p.cbm.toLocaleString(localeTag, { maximumFractionDigits: 4 })} m³` : null} />
                                <Field label={t('product.plates_count')} value={p.plates_count != null ? `${p.plates_count}` : null} />
                            </dl>
                            {p.box_size && (
                                <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4 dark:border-zinc-800 dark:bg-zinc-800/50">
                                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-zinc-600">{t('v2.common.box_dimensions_mm')}</p>
                                    <pre className="font-mono text-xs leading-relaxed text-gray-700 dark:text-zinc-300">
                                        {p.box_size.split(/\s+(?=A:|B:|C:|D:)/).filter(Boolean).join('\n')}
                                    </pre>
                                </div>
                            )}
                        </div>
                        <div className={cardCls + ' p-5'}>
                            <p className={sectionTitleCls}>{t('v2.common.weight_and_assembly')}</p>
                            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                <Field label={t('product.assembled_mm')}  value={p.assembled_size} />
                                <Field label={t('product.net_weight')}    value={p.net_weight_kg != null ? `${p.net_weight_kg} kg` : null} />
                                <Field label={t('v2.common.gross_with_box')} value={p.gross_weight_kg != null ? `${p.gross_weight_kg} kg` : null} />
                                <Field label={t('v2.common.weight_tower')} value={p.weight_tower_qty != null ? `${p.weight_tower_qty} un.` : null} />
                            </dl>
                        </div>
                    </div>
                )}

                {/* CORES */}
                {tab === 'colors' && (
                    <div className="grid gap-5 sm:grid-cols-2">
                        {[
                            { key: 'frame',    label: t('v2.common.structure_frame') },
                            { key: 'seat',     label: t('v2.common.upholstery')      },
                            { key: 'stitches', label: t('v2.common.stitches')        },
                            { key: 'arm',      label: t('v2.common.arm_support')     },
                        ].map(({ key, label }) => (
                            <div key={key} className={cardCls + ' p-5'}>
                                <p className={sectionTitleCls}>{label}</p>
                                <ColorSwatchList items={p.color_options[key] ?? []} empty={t('product.no_colors')} />
                            </div>
                        ))}
                        <div className={cardCls + ' p-5 sm:col-span-2'}>
                            <p className={sectionTitleCls}>{t('product.color_photos')}</p>
                            <ProductColorPhotos productId={p.id} />
                        </div>
                    </div>
                )}

                {/* FORNECEDOR */}
                {tab === 'supplier' && (
                    <div className={cardCls + ' p-5'}>
                        <dl className="grid gap-4 sm:grid-cols-3">
                            <Field label={t('common.supplier')}         value={p.supplier} />
                            <Field label={t('v2.common.supplier_code')} value={<span className="font-mono">{p.supplier_code}</span>} />
                            <Field label={t('product.protheus_code')}   value={<span className="font-mono">{p.protheus_code}</span>} />
                        </dl>
                    </div>
                )}

                {/* DESCRIÇÃO */}
                {tab === 'description' && (
                    <div className="space-y-5">
                        {p.description && (
                            <div className={cardCls + ' p-5'}>
                                <p className={sectionTitleCls}>{t('common.description')}</p>
                                <div
                                    className="prose prose-sm max-w-none text-gray-700 dark:prose-invert dark:text-zinc-300"
                                    dangerouslySetInnerHTML={{ __html: p.description }}
                                />
                            </div>
                        )}
                        <div className={cardCls + ' p-5'}>
                            <p className={sectionTitleCls}>Descrições Aduaneiras</p>
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <Field label={t('product.customs_desc_pt')} value={p.customs_descriptions.pt_BR} />
                                <Field label={t('product.customs_desc_en')} value={p.customs_descriptions.en} />
                                <Field label={t('product.customs_desc_es')} value={p.customs_descriptions.es} />
                                <Field label={t('v2.common.customs_default')} value={p.customs_description_default} />
                            </dl>
                        </div>
                    </div>
                )}

                {/* TRADUÇÕES */}
                {tab === 'translations' && (
                    <TranslationsTab
                        productId={id!}
                        productName={p.name}
                        productCode={p.code}
                        translations={translationsQuery.data ?? []}
                        isLoading={translationsQuery.isLoading}
                    />
                )}
            </div>
        </div>
    );
}

/* ─── TranslationsTab ────────────────────────────────────────── */
function TranslationCard({
    productId,
    translation,
    isOnlyDefault,
}: {
    productId: string;
    translation: ProductTranslation;
    isOnlyDefault: boolean;
}) {
    const qc = useQueryClient();
    const [form, setForm] = useState({
        translated_name: translation.translated_name ?? '',
        functional_description: translation.functional_description ?? '',
        notes: translation.notes ?? '',
        is_default: translation.is_default,
    });
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setForm({
            translated_name: translation.translated_name ?? '',
            functional_description: translation.functional_description ?? '',
            notes: translation.notes ?? '',
            is_default: translation.is_default,
        });
    }, [translation]);

    const { mutate, isPending } = useMutation({
        mutationFn: () => api.put(`/products/${productId}/translations/${translation.locale}`, form),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['products', productId, 'translations'] });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        },
    });

    const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200 placeholder:text-gray-300 dark:placeholder:text-zinc-700';
    const isDirty = form.translated_name !== (translation.translated_name ?? '')
        || form.functional_description !== (translation.functional_description ?? '')
        || form.notes !== (translation.notes ?? '')
        || form.is_default !== translation.is_default;

    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
            {/* Header do idioma */}
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3 dark:border-zinc-800 dark:bg-zinc-800/30">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-black text-gray-800 dark:text-zinc-100">
                        {LOCALE_LABELS[translation.locale] ?? translation.locale}
                    </span>
                    {form.is_default && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                            <PiStarBold className="size-3" /> Padrão para documentos
                        </span>
                    )}
                    {translation.id && (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400">
                            Salvo
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {!form.is_default && (
                        <button type="button"
                            onClick={() => setForm((f) => ({ ...f, is_default: true }))}
                            className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-[11px] font-bold text-gray-400 hover:border-amber-300 hover:text-amber-600 dark:border-zinc-700 dark:hover:text-amber-400"
                            title="Definir como padrão para documentos"
                        >
                            <PiStarBold className="size-3" /> Definir padrão
                        </button>
                    )}
                    <button type="button"
                        onClick={() => mutate()}
                        disabled={isPending || (!isDirty && !saved)}
                        className={cn(
                            'flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-black transition',
                            saved
                                ? 'bg-emerald-500 text-white'
                                : isDirty
                                    ? 'bg-amber-500 text-white hover:bg-amber-600 dark:bg-[#C9A84C] dark:text-zinc-900'
                                    : 'border border-gray-200 text-gray-400 dark:border-zinc-700',
                        )}
                    >
                        {isPending
                            ? <PiArrowsClockwiseBold className="size-3.5 animate-spin" />
                            : saved
                                ? <PiCheckCircleBold className="size-3.5" />
                                : <PiFloppyDiskBold className="size-3.5" />
                        }
                        {isPending ? 'Salvando…' : saved ? 'Salvo!' : 'Salvar'}
                    </button>
                </div>
            </div>

            {/* Campos */}
            <div className="grid gap-4 p-5 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                        Tradução do nome
                    </label>
                    <input
                        className={inputCls}
                        value={form.translated_name}
                        onChange={(e) => setForm((f) => ({ ...f, translated_name: e.target.value }))}
                        placeholder="Ex: Crossover Duplo Funcional"
                    />
                </div>
                <div className="flex flex-col gap-1.5 sm:row-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                        Descrição funcional
                        <span className="ml-1.5 font-normal normal-case text-gray-300 dark:text-zinc-700">usada em documentos</span>
                    </label>
                    <textarea
                        rows={4}
                        className={cn(inputCls, 'resize-none')}
                        value={form.functional_description}
                        onChange={(e) => setForm((f) => ({ ...f, functional_description: e.target.value }))}
                        placeholder="Ex: Estação com cabos ajustáveis para treinos de força versáteis e funcionais"
                    />
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                        Observações internas
                    </label>
                    <input
                        className={inputCls}
                        value={form.notes}
                        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="Anotações sobre esta tradução…"
                    />
                </div>
            </div>
        </div>
    );
}

function TranslationsTab({ productId, productName, productCode, translations, isLoading }: {
    productId: string;
    productName: string;
    productCode: string | null;
    translations: ProductTranslation[];
    isLoading: boolean;
}) {
    const cardCls = 'rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[1, 2].map((i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-gray-100 dark:bg-zinc-800" />)}
            </div>
        );
    }

    const ptBR = translations.find((t) => t.locale === 'pt_BR');
    const hasDefault = translations.some((t) => t.is_default);

    return (
        <div className="space-y-5">
            {/* Info box */}
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4 dark:border-amber-500/20 dark:bg-amber-500/8">
                <PiTranslateBold className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                    <p className="font-black">Nome original: <span className="font-mono">{productCode && `[${productCode}]`} {productName}</span></p>
                    <p className="mt-1 text-xs text-amber-700/70 dark:text-amber-400/60">
                        A tradução marcada como <strong>Padrão</strong> é usada automaticamente em documentos de exportação (Purchase Orders, PI, etiquetas).
                        A <strong>Descrição Funcional</strong> aparece em catálogos e documentos comerciais.
                    </p>
                    {ptBR?.translated_name && (
                        <p className="mt-2 text-xs font-black text-amber-700 dark:text-amber-300">
                            Tradução PT-BR ativa: "{ptBR.translated_name}"
                        </p>
                    )}
                </div>
            </div>

            {/* Tradução PT-BR primeiro (a mais importante) */}
            {translations
                .sort((a, b) => {
                    if (a.locale === 'pt_BR') return -1;
                    if (b.locale === 'pt_BR') return 1;
                    return a.locale.localeCompare(b.locale);
                })
                .map((tr) => (
                    <TranslationCard
                        key={tr.locale}
                        productId={productId}
                        translation={tr}
                        isOnlyDefault={hasDefault && tr.is_default && translations.filter((t) => t.is_default).length === 1}
                    />
                ))}
        </div>
    );
}
